import type { ProjectContentMatch } from "@cmmd-forge/contracts";
import type { KeyboardEventHandler, ReactNode, RefObject } from "react";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Spinner } from "~/components/ui/spinner";
import { Toggle } from "~/components/ui/toggle";
import { FileSearchIcon } from "lucide-react";

import type {
  ProjectContentSearchEmptyState,
  ProjectContentSearchPresentation,
  ProjectContentSearchStatus,
} from "./projectContentSearchDialog.logic";
import { ProjectContentSearchResults } from "./ProjectContentSearchResults";

export type ProjectContentSearchConnectionAction =
  | { readonly kind: "ready"; readonly onConnect: () => void }
  | { readonly kind: "connecting" }
  | { readonly kind: "failed"; readonly onConnect: () => void };

function SearchOptionToggle(props: {
  readonly children: ReactNode;
  readonly label: string;
  readonly onPressedChange: (pressed: boolean) => void;
  readonly pressed: boolean;
}) {
  return (
    <Toggle
      aria-label={props.label}
      onPressedChange={props.onPressedChange}
      pressed={props.pressed}
      size="segmented"
      variant="segmented"
    >
      {props.children}
    </Toggle>
  );
}

export function ProjectContentSearchControls(props: {
  readonly caseSensitive: boolean;
  readonly onCaseSensitiveChange: (pressed: boolean) => void;
  readonly onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  readonly onQueryChange: (query: string) => void;
  readonly onUseRegexChange: (pressed: boolean) => void;
  readonly onWholeWordChange: (pressed: boolean) => void;
  readonly query: string;
  readonly useRegex: boolean;
  readonly wholeWord: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
      <Input
        aria-label="Search project files"
        autoFocus
        onChange={(event) => props.onQueryChange(event.target.value)}
        onKeyDown={props.onKeyDown}
        placeholder="Find in files"
        type="search"
        value={props.query}
      />
      <div
        aria-label="Search options"
        className="grid shrink-0 grid-cols-3 rounded-lg border border-border p-0.5"
      >
        <SearchOptionToggle
          label="Match case"
          onPressedChange={props.onCaseSensitiveChange}
          pressed={props.caseSensitive}
        >
          Aa
        </SearchOptionToggle>
        <SearchOptionToggle
          label="Match whole word"
          onPressedChange={props.onWholeWordChange}
          pressed={props.wholeWord}
        >
          ab
        </SearchOptionToggle>
        <SearchOptionToggle
          label="Use regular expression"
          onPressedChange={props.onUseRegexChange}
          pressed={props.useRegex}
        >
          .*
        </SearchOptionToggle>
      </div>
    </div>
  );
}

export function ProjectContentSearchStatus(props: { readonly status: ProjectContentSearchStatus }) {
  if (props.status.kind === "hidden") return null;
  return (
    <div
      aria-live="polite"
      className="flex min-h-9 shrink-0 items-center border-b border-border px-4 text-sm text-muted-foreground"
    >
      <ProjectContentSearchStatusMessage status={props.status} />
    </div>
  );
}

function ProjectContentSearchStatusMessage(props: { readonly status: ProjectContentSearchStatus }) {
  switch (props.status.kind) {
    case "loading":
      return <ProjectContentSearchLoadingMessage />;
    case "error":
      return <ProjectContentSearchErrorMessage message={props.status.message} />;
    case "invalid-regex":
      return (
        <ProjectContentSearchErrorMessage
          message={`Invalid regular expression: ${props.status.message}`}
        />
      );
    case "summary":
      return <ProjectContentSearchSummaryMessage status={props.status} />;
    case "hidden":
      return null;
  }
}

function ProjectContentSearchLoadingMessage() {
  return (
    <span className="flex items-center gap-2">
      <Spinner className="size-4 motion-reduce:animate-none" />
      Searching files
    </span>
  );
}

function ProjectContentSearchErrorMessage(props: { readonly message: string }) {
  return (
    <span className="text-destructive-foreground" role="alert">
      {props.message}
    </span>
  );
}

function ProjectContentSearchSummaryMessage(props: {
  readonly status: Extract<ProjectContentSearchStatus, { readonly kind: "summary" }>;
}) {
  return (
    <span>
      {props.status.resultCount.toLocaleString()} result
      {props.status.resultCount === 1 ? "" : "s"} in {props.status.fileCount.toLocaleString()} file
      {props.status.fileCount === 1 ? "" : "s"}
      {props.status.truncated ? ". Refine the search for more." : "."}
    </span>
  );
}

export function ProjectContentSearchBody(props: {
  readonly connectionAction: ProjectContentSearchConnectionAction;
  readonly onOpenMatch: (match: ProjectContentMatch) => void;
  readonly onSelect: (index: number) => void;
  readonly presentation: ProjectContentSearchPresentation;
  readonly selectedIndex: number;
  readonly selectedResultRef: RefObject<HTMLButtonElement | null>;
}) {
  const message = emptyStateMessage(props.presentation.emptyState);
  return message ? (
    <ContentSearchEmptyState
      connectionAction={props.connectionAction}
      emptyState={props.presentation.emptyState}
      message={message}
    />
  ) : (
    <ProjectContentSearchResults
      disabled={!props.presentation.canOpenMatches}
      matches={props.presentation.matches}
      onOpenMatch={props.onOpenMatch}
      onSelect={props.onSelect}
      selectedIndex={props.selectedIndex}
      selectedResultRef={props.selectedResultRef}
    />
  );
}

function emptyStateMessage(emptyState: ProjectContentSearchEmptyState): string | null {
  if (emptyState === "disconnected") return "Connect this environment to search its files.";
  if (emptyState === "empty-query") return "Type to search across this checkout.";
  if (emptyState === "no-results") return "No files contain that search.";
  return null;
}

function ContentSearchEmptyState(props: {
  readonly connectionAction: ProjectContentSearchConnectionAction;
  readonly emptyState: ProjectContentSearchEmptyState;
  readonly message: string;
}) {
  const isDisconnected = props.emptyState === "disconnected";
  const action = props.connectionAction;
  return (
    <Empty className="gap-3 px-6 py-10 md:px-12">
      <EmptyMedia variant="icon">
        <FileSearchIcon />
      </EmptyMedia>
      <EmptyHeader>
        <EmptyTitle className="text-base">Search project files</EmptyTitle>
        <EmptyDescription>{props.message}</EmptyDescription>
      </EmptyHeader>
      {isDisconnected ? <ProjectContentSearchConnectAction action={action} /> : null}
    </Empty>
  );
}

function ProjectContentSearchConnectAction(props: {
  readonly action: ProjectContentSearchConnectionAction;
}) {
  if (props.action.kind === "connecting") {
    return (
      <Button aria-label="Connecting" disabled>
        <Spinner className="size-4 motion-reduce:animate-none" />
        Connecting
      </Button>
    );
  }

  const failed = props.action.kind === "failed";
  return (
    <>
      {failed ? (
        <p className="text-sm text-destructive-foreground" role="alert">
          Could not connect to this environment. Retry the connection.
        </p>
      ) : null}
      <Button onClick={props.action.onConnect}>{failed ? "Retry connection" : "Connect"}</Button>
    </>
  );
}
